import {
    ColDef,
    ICellRendererParams,
    IsFullWidthRowParams,
    ValueFormatterFunc,
    ValueFormatterParams,
    ValueGetterParams
} from "ag-grid-community";
import { BaseAgGrid } from "../tables/base/BaseAgGrid";
import { ABTag } from "./ABTag";
import css from "./CompareMetricsTable.module.scss";
import { CompareDeltaTag } from "./CompareTable";
import { DeltaFormat } from "./comparisons";

export type CompareMetric = {
    key: string;
    label: string;
    formatter?: ValueFormatterFunc;
    a: number | null;
    b: number | null;
    deltaFormat?: DeltaFormat;
};

type CompareMetricSection = {
    key: string;
    label: string;
    subtitle?: string;
    metrics: ReadonlyArray<CompareMetric>;
};

type SectionRowData = {
    kind: "section";
    key: string;
    label: string;
    subtitle?: string;
};

type MetricRowData = CompareMetric & { kind: "metric" };

type RowData = SectionRowData | MetricRowData;

const buildRows = (
    sections: ReadonlyArray<CompareMetricSection>
): RowData[] => {
    const rows: RowData[] = [];
    sections.forEach((section) => {
        rows.push({
            kind: "section",
            key: `section:${section.key}`,
            label: section.label,
            subtitle: section.subtitle
        });
        section.metrics.forEach((m) => {
            rows.push({ ...m, kind: "metric" });
        });
    });
    return rows;
};

const HeaderA: React.FC = () => <ABTag scenario="a" />;
const HeaderB: React.FC = () => <ABTag scenario="b" />;

const SectionFullWidthRenderer: React.FC<{ data?: RowData }> = ({ data }) => {
    if (!data || data.kind !== "section") {
        return null;
    }
    return (
        <div className={css["section-fullwidth"]}>
            <strong className={css["section-label"]}>{data.label}</strong>
            {data.subtitle && (
                <span className={css["section-subtitle"]}>
                    {` · ${data.subtitle}`}
                </span>
            )}
        </div>
    );
};

const metricLabelGetter = (params: ValueGetterParams<RowData>): string => {
    const d = params.data;
    return d && d.kind === "metric" ? d.label : "";
};

const formatValueCell: ValueFormatterFunc<RowData> = (params) => {
    const row = params.data;
    if (!row || row.kind !== "metric") {
        return "";
    }
    return row.formatter ? row.formatter(params) : String(params.value ?? "-");
};

const valueGetterA = (params: ValueGetterParams<RowData>): number | null => {
    const d = params.data;
    return d && d.kind === "metric" ? d.a : null;
};

const valueGetterB = (params: ValueGetterParams<RowData>): number | null => {
    const d = params.data;
    return d && d.kind === "metric" ? d.b : null;
};

const DeltaCellRenderer = (
    params: ICellRendererParams<RowData>
): JSX.Element | null => {
    const m = params.data;
    if (!m || m.kind !== "metric") {
        return null;
    }
    const { formatter } = m;

    const formatAbsolute = formatter
        ? (n: number) => formatter({ value: n } as ValueFormatterParams)
        : undefined;
    return (
        <CompareDeltaTag
            a={m.a}
            b={m.b}
            format={m.deltaFormat ?? "both"}
            formatAbsolute={formatAbsolute}
        />
    );
};

const columnDefs: ColDef<RowData>[] = [
    {
        headerName: "Metric",
        flex: 1,
        sortable: false,
        valueGetter: metricLabelGetter
    },
    {
        headerName: "A",
        width: 100,
        sortable: false,
        type: "rightAligned",
        pinned: "right",
        headerComponent: HeaderA,
        valueGetter: valueGetterA,
        valueFormatter: formatValueCell
    },
    {
        headerName: "B",
        width: 100,
        sortable: false,
        type: "rightAligned",
        pinned: "right",
        headerComponent: HeaderB,
        valueGetter: valueGetterB,
        valueFormatter: formatValueCell
    },
    {
        headerName: "Δ",
        width: 180,
        sortable: false,
        type: "rightAligned",
        pinned: "right",
        cellRenderer: DeltaCellRenderer
    }
];

export const CompareMetricsTable: React.FC<{
    sections: ReadonlyArray<CompareMetricSection>;
}> = ({ sections }) => {
    const rows = buildRows(sections);
    return (
        <div className={css["wrap"]}>
            <BaseAgGrid<RowData>
                loadingData={rows}
                columnDefs={columnDefs}
                getRowId={(params) => params.data.key}
                height="auto"
                rowHeight={30}
                headerHeight={28}
                isFullWidthRow={(params: IsFullWidthRowParams<RowData>) =>
                    params.rowNode.data?.kind === "section"
                }
                fullWidthCellRenderer={SectionFullWidthRenderer}
            />
        </div>
    );
};
